import type { Badge } from '@nordhealth/components'
import type {
  Schemas,
  ResponseByPath,
  QueryParamsByPath,
  BodyByPath,
} from '@@/api/types'
import type { IncludedResourceMap } from '@/utils/jsonapi.js'
import { FILE_EXTENSIONS } from '@/utils/file-type.js'

export type ThreadIncludedMap = IncludedResourceMap<
  | 'ConversationParticipant'
  | 'ConversationMessage'
  | 'ConversationMessageReceipt'
  | 'ConversationMessageMedia'
  | 'User'
>

type UserAttributes = NonNullable<Schemas['User']['attributes']>

export type ParticipantAttributes = NonNullable<
  Schemas['ConversationParticipant']['attributes']
>

export type ConversationListItem = Schemas['ConversationThreadList']

export const CONVERSATIONS_LIST_URL = '/{provet_id}/api/conversations/threads'
export type ConversationListResponse = ResponseByPath<
  'get',
  typeof CONVERSATIONS_LIST_URL
>
export type ConversationListQueryParams = QueryParamsByPath<
  'get',
  typeof CONVERSATIONS_LIST_URL
>

// Used to start a NEW conversation with a client. Requires `client_id` in body
// to identify the recipient and creates a new thread automatically.
export const CONVERSATION_MESSAGES_URL =
  '/{provet_id}/api/conversations/messages'
export type SendMessageBody = BodyByPath<
  'post',
  typeof CONVERSATION_MESSAGES_URL
>

// Used to send a message to an EXISTING open thread. The thread ID is in the URL,
// so no `client_id` is needed - just `channel` and `content`.
export const CONVERSATION_THREAD_MESSAGES_URL =
  '/{provet_id}/api/conversations/threads/{id}/messages'
export type SendMessageToThreadBody = BodyByPath<
  'post',
  typeof CONVERSATION_THREAD_MESSAGES_URL
>

export const CONVERSATION_THREAD_DETAIL_URL =
  '/{provet_id}/api/conversations/threads/{id}'
export type ConversationThreadDetailResponse = ResponseByPath<
  'get',
  typeof CONVERSATION_THREAD_DETAIL_URL
>
export type ConversationThreadDetailQueryParams = QueryParamsByPath<
  'get',
  typeof CONVERSATION_THREAD_DETAIL_URL
>

export const MESSAGE_CONSTRAINTS = {
  MAX_LENGTH: 1000,
  MIN_LENGTH: 1,
} as const

/** Max attachment file size: 10 MB */
export const CONVERSATION_ATTACHMENT_MAX_SIZE = 10 * 1024 * 1024

/** Max number of attachments per message */
export const CONVERSATION_ATTACHMENT_MAX_FILES = 6

/** Allowed file extensions for conversation attachments */
export const CONVERSATION_ATTACHMENT_EXTENSIONS = FILE_EXTENSIONS

/** Allowed extensions as comma-separated string for file input accept attribute */
export const CONVERSATION_ATTACHMENT_ACCEPT_STRING = [
  ...FILE_EXTENSIONS.IMAGE,
  ...FILE_EXTENSIONS.PDF,
  ...FILE_EXTENSIONS.DOC,
].join()
export type MessageConstraints = typeof MESSAGE_CONSTRAINTS

export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
} as const
export type PaginationConfig = typeof PAGINATION

export const MAX_UNREAD_COUNT = 9 as const

export const ARIA_TRUNCATED_MESSAGE_LENGTH = 50 as const

export const assignedUserTooltipId = (conversationId: string) =>
  `assigned-user-tooltip-${conversationId}`

export const ConversationStatus = {
  Open: 'OPEN',
  Resolved: 'RESOLVED',
} as const

export type ConversationStatusValue =
  (typeof ConversationStatus)[keyof typeof ConversationStatus]
export type ConversationStatusKey = keyof typeof ConversationStatus

export const ConversationPriority = {
  Low: 'LOW',
  Normal: 'NORMAL',
  High: 'HIGH',
} as const

export type ConversationPriorityValue =
  (typeof ConversationPriority)[keyof typeof ConversationPriority]
export type ConversationPriorityKey = keyof typeof ConversationPriority

export const PRIORITY_BADGE_VARIANT: Record<
  ConversationPriorityValue,
  Badge['variant']
> = {
  LOW: 'neutral',
  NORMAL: 'warning',
  HIGH: 'danger',
} as const

export const MessageReceiptStatus = {
  Failed: 'failed',
  Pending: 'pending',
  Sent: 'sent',
  Delivered: 'delivered',
  Read: 'read',
} as const

export type MessageReceiptStatusKey = keyof typeof MessageReceiptStatus
export type MessageReceiptStatusValue =
  (typeof MessageReceiptStatus)[MessageReceiptStatusKey]

export const MessageErrorCode = {
  InvalidPhoneNumber: 'INVALID_PHONE_NUMBER',
  ClientOptedOut: 'CLIENT_OPTED_OUT',
  PhoneUnreachable: 'PHONE_UNREACHABLE',
  BlockedByCarrier: 'BLOCKED_BY_CARRIER',
  MessageTooLong: 'MESSAGE_TOO_LONG',
  ConfigurationError: 'CONFIGURATION_ERROR',
  ProviderUnavailable: 'PROVIDER_UNAVAILABLE',
  UnknownError: 'UNKNOWN_ERROR',
} as const

export type MessageErrorCodeKey = keyof typeof MessageErrorCode
export type MessageErrorCodeValue =
  (typeof MessageErrorCode)[MessageErrorCodeKey]

export const MessageDirection = {
  Inbound: 'inbound',
  Outbound: 'outbound',
} as const

export type MessageDirectionKey = keyof typeof MessageDirection
export type MessageDirectionValue =
  (typeof MessageDirection)[keyof typeof MessageDirection]

export const ConversationViewMode = {
  Viewing: 'viewing',
  New: 'new',
} as const

export type ConversationViewModeValue =
  (typeof ConversationViewMode)[keyof typeof ConversationViewMode]
export type ConversationViewModeKey = keyof typeof ConversationViewMode

export type ParticipantInfo = {
  readonly first_name: string
  readonly last_name: string
  readonly initials: string
  readonly phone_number: string
  readonly avatar: string
}

export type ParticipantType = 'employee' | 'client' | 'system' | 'bot'

export type ClientParticipant = {
  readonly participant_type: ParticipantType
  readonly participant_internal_id: number | null
  readonly created_at?: string
  readonly updated_at?: string
  readonly info: ParticipantInfo
}

export type ParticipantIncluded = {
  readonly type: 'ConversationParticipant'
  readonly id: string
  readonly attributes: ClientParticipant
}

export type MessageIncluded = {
  readonly type: 'ConversationMessage'
  readonly id: string
  readonly attributes: {
    readonly content: string
    readonly external_id: string
    readonly message_type: string
    readonly direction: string
    readonly created_at: string
    readonly updated_at: string
  }
}

export type MessageSender = {
  readonly first_name?: string
  readonly last_name?: string
  readonly phone_number?: string
}

export type MessageReceipt = {
  readonly status?: MessageReceiptStatusValue
  readonly error_code?: MessageErrorCodeValue | null
  readonly created_at?: string
  readonly updated_at?: string
}

type MessageMediaAttributes = NonNullable<
  Schemas['ConversationMessageMedia']['attributes']
>
export type MessageMediaType = MessageMediaAttributes['media_type']

export type MessageMedia = {
  readonly id: string
  readonly url: string
  readonly file_name: string
  readonly file_size: number
  readonly mime_type: string
  readonly media_type: MessageMediaType
  readonly is_file_available: boolean
}

export type MessageWithSender = MessageIncluded & {
  readonly sender?: MessageSender
  readonly receipts?: MessageReceipt[]
  readonly media?: MessageMedia[]
}

export type AssignedUser = Pick<
  UserAttributes,
  'first_name' | 'last_name' | 'initials'
> & {
  readonly id: string
}

type ConversationListResponseItem = ConversationListResponse['data'][number]

export type NormalizedConversationAttributes =
  ConversationListResponseItem['attributes'] & {
    readonly id: string
  }

export interface NormalizedConversation extends Omit<
  ConversationListResponseItem,
  'attributes'
> {
  readonly attributes: NormalizedConversationAttributes
  readonly client?: ClientParticipant
  readonly assignedUser?: AssignedUser
  readonly patientId?: string
}

/**
 * Converts a thread detail response into a NormalizedConversation.
 * ConversationThreadDetail and ConversationThreadList attributes are structurally
 * identical but TypeScript treats them as distinct types since they come from
 * different API endpoint schemas, hence the cast on attributes.
 */
type NormalizeThreadDetailToConversationOptions = {
  id: string
  attributes: NonNullable<
    ConversationThreadDetailResponse['data']['attributes']
  >
  client?: ClientParticipant
  assignedUser?: AssignedUser
  patientId?: string
}

export function normalizeThreadDetailToConversation(
  options: NormalizeThreadDetailToConversationOptions,
): NormalizedConversation {
  return {
    id: options.id,
    type: 'ConversationThread',
    attributes: {
      ...options.attributes,
      id: options.id,
    } satisfies NormalizedConversationAttributes,
    client: options.client,
    assignedUser: options.assignedUser,
    patientId: options.patientId,
  }
}

/**
 * Extracts client and assigned user from thread detail participants.
 * Used when building a NormalizedConversation from thread detail data
 * (e.g., when the selected conversation is not in the current list page).
 */
export function extractParticipants(participants: ParticipantIncluded[]): {
  client?: ClientParticipant
  assignedUser?: AssignedUser
} {
  const clientParticipant = participants.find(
    (p) => p.attributes?.participant_type === 'client',
  )

  const assignedUserParticipant = participants.find(
    (p) => p.attributes?.participant_type === 'employee',
  )

  const assignedUser = assignedUserParticipant
    ? {
        id: assignedUserParticipant.id,
        first_name: assignedUserParticipant.attributes?.info?.first_name ?? '',
        last_name: assignedUserParticipant.attributes?.info?.last_name ?? '',
        initials: assignedUserParticipant.attributes?.info?.initials ?? '',
      }
    : undefined

  return {
    client: clientParticipant?.attributes,
    assignedUser,
  }
}

export type AssignedUserFromIncluded = {
  readonly id: string
  readonly first_name?: string
  readonly last_name?: string
  readonly is_active?: boolean
  readonly avatar_image_url?: string
  readonly avatar_initials?: string
}

export type NormalizedConversationThreadResponse = {
  data: ConversationThreadDetailResponse['data']
  participants: ParticipantIncluded[]
  messages: MessageWithSender[]
  patientId?: string
  assignedUser?: AssignedUserFromIncluded
}

export type NormalizedConversationListResponse = Omit<
  ConversationListResponse,
  'data'
> & {
  data: NormalizedConversation[]
}

export type ConversationListFilters = {
  search?: string
  status?: ConversationListQueryParams['filter[status]']
  priority?: ConversationListQueryParams['filter[priority]']
  client?: ConversationListQueryParams['filter[participant_client]']
  assignedUser?: ConversationListQueryParams['filter[assigned_to_user]']
}

export type ConversationMessage = NonNullable<
  Schemas['ConversationSendMessageToClientRequest']['data']['attributes']
>

export const ChatFormDisabledReason = {
  NoSms: 'noSms',
  Landline: 'landline',
  MissingPhoneNumber: 'missingPhoneNumber',
} as const
export type ChatFormDisabledReason =
  (typeof ChatFormDisabledReason)[keyof typeof ChatFormDisabledReason]
